import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { XCircleIcon } from '@heroicons/react/24/solid';

interface UploadAreaProps {
  onFilesChange: (files: File[]) => void;
  initialFiles?: File[];
  maxFiles?: number;
  accept?: Record<string, string[]>;
}

const UploadArea: React.FC<UploadAreaProps> = ({ onFilesChange, initialFiles = [], maxFiles = 10, accept = { 'image/*': ['.jpeg', '.png', '.gif', '.webp', '.jpg'] } }) => {
  const [files, setFiles] = useState<File[]>(initialFiles);

  useEffect(() => {
    onFilesChange(files);
  }, [files, onFilesChange]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Filter out files that exceed maxFiles
    const newFiles = [...files, ...acceptedFiles].slice(0, maxFiles);
    setFiles(newFiles);
  }, [files, maxFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles: maxFiles - files.length, // Only allow dropping up to the remaining limit
    disabled: files.length >= maxFiles,
  });

  const removeFile = (fileToRemove: File) => {
    setFiles(prevFiles => prevFiles.filter(file => file !== fileToRemove));
  };

  return (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
      <div {...getRootProps()} className="cursor-pointer">
        <input {...getInputProps()} />
        {
          isDragActive ?
            <p className="text-gray-600">Déposez les fichiers ici...</p> :
            <p className="text-gray-600">Glissez-déposez des fichiers ici, ou cliquez pour sélectionner</p>
        }
        {files.length > 0 && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {files.map((file, index) => (
              <div key={index} className="relative w-full h-32 rounded-lg overflow-hidden shadow-md">
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="w-full h-full object-cover"
                  onLoad={() => { URL.revokeObjectURL(file.name); }} // Clean up memory
                />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeFile(file); }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 text-xs opacity-80 hover:opacity-100"
                  aria-label="Remove file"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
                  {file.name}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {files.length >= maxFiles && (
        <p className="text-sm text-red-500 mt-2">Limite de {maxFiles} fichiers atteinte.</p>
      )}
    </div>
  );
};

export default UploadArea;
